import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Container,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  alpha,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  SwapHoriz as TransferIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Store as StoreIcon,
  Inventory as InventoryIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createTransfer, getAvailableInventory } from '../services/inventoryTransferService';
import { getStoresFiltered, getMainStore } from '../../stores/services/storeService';
import ProductSelect from '../../invoices/components/ProductSelect';
import { searchProducts } from '../../product/services/productService';

function CreateTransferPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [mainStore, setMainStore] = useState(null);
  const debounceRef = React.useRef();
  
  const [formData, setFormData] = useState({
    transferDate: new Date().toISOString().split('T')[0],
    sourceStoreId: '',
    destinationStoreId: '',
    transferReason: '',
    items: [],
  });

  useEffect(() => {
    loadStores();
    loadMainStore();
  }, []);

  const loadStores = async () => {
    try {
      const response = await getStoresFiltered({ includeVirtual: false, includeInactive: false });
      const storesData = response?.payload || response || [];
      setStores(Array.isArray(storesData) ? storesData : []);
    } catch (error) {
      toast.error('Failed to load stores');
    }
  };

  const loadMainStore = async () => {
    try {
      const response = await getMainStore();
      const store = response?.payload || response;
      setMainStore(store);
      // Auto-select main store as source
      if (store?.id || store?.Id) {
        setFormData((prev) => ({
          ...prev,
          sourceStoreId: store.id || store.Id,
        }));
      }
    } catch (error) {
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          productId: null,
          productName: '',
          quantityToTransfer: 0,
          transferSource: 'MainStore',
          notes: '',
          availability: null,
        },
      ],
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedItems = [...prev.items];
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
      };
      return { ...prev, items: updatedItems };
    });
  };

  const handleProductSelect = async (index, product) => {
    if (!product) return;

    handleItemChange(index, 'productId', product.Id || product.value);
    handleItemChange(index, 'productName', product.Name || product.label);

    // Fetch availability
    if (formData.sourceStoreId && (product.Id || product.value)) {
      try {
        const response = await getAvailableInventory({
          productId: product.Id || product.value,
          sourceStoreId: formData.sourceStoreId,
        });
        const availability = response?.payload || response;
        handleItemChange(index, 'availability', availability);
      } catch (error) {
        toast.error('Failed to fetch product availability');
      }
    }
  };

  // Load product options with debounce (same API as Sale Order)
  const loadProductOptions = (inputValue, callback) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!inputValue) return callback([]);

      try {
        // Check if input is barcode (8, 12, 13, or 14 digits)
        const barcodeLengths = [8, 12, 13, 14];
        let paramsObj;

        if (barcodeLengths.includes(inputValue.length) && /^\d+$/.test(inputValue)) {
          // Search by barcode
          paramsObj = { skuBarCode: inputValue };
        } else if (/^\d+$/.test(inputValue)) {
          // Search by ID or barcode
          paramsObj = { skuBarCode: inputValue };
        } else if (inputValue.length >= 3) {
          // Search by name (minimum 3 characters)
          paramsObj = { name: inputValue };
        } else {
          return callback([]);
        }

        const data = await searchProducts(paramsObj);

        const productsArray = Array.isArray(data?.payload)
          ? data.payload
          : Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];

        // Limit to 50 results for performance
        const limitedProducts = productsArray.slice(0, 50);

        const productOptions = limitedProducts.map((p) => ({
          value: p.Id,
          label: `${p.Name} (€${p.SalePrice || 0})${p.SKU ? ' - SKU: ' + p.SKU : ''}${
            p.BarCode ? ' - BarCode: ' + p.BarCode : ''
          }`,
          ...p,
        }));

        callback(productOptions);
      } catch (error) {
        callback([]);
      }
    }, 350); // 350ms debounce delay
  };

  const validateForm = () => {
    if (!formData.sourceStoreId) {
      toast.error('Please select a source store');
      return false;
    }
    if (!formData.destinationStoreId) {
      toast.error('Please select a destination store');
      return false;
    }
    if (formData.sourceStoreId === formData.destinationStoreId) {
      toast.error('Source and destination stores cannot be the same');
      return false;
    }
    if (formData.items.length === 0) {
      toast.error('Please add at least one item to transfer');
      return false;
    }

    // Validate each item
    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i];
      if (!item.productId) {
        toast.error(`Please select a product for item ${i + 1}`);
        return false;
      }
      if (!item.quantityToTransfer || item.quantityToTransfer <= 0) {
        toast.error(`Please enter a valid quantity for ${item.productName}`);
        return false;
      }
      if (item.availability) {
        const maxAvailable =
          item.transferSource === 'MainStore'
            ? item.availability.officialInventory
            : item.availability.unofficialInventory;
        if (item.quantityToTransfer > maxAvailable) {
          toast.error(
            `Quantity for ${item.productName} exceeds available inventory (${maxAvailable})`
          );
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      const transferData = {
        transferDate: formData.transferDate,
        sourceStoreId: Number(formData.sourceStoreId),
        destinationStoreId: Number(formData.destinationStoreId),
        transferReason: formData.transferReason || null,
        items: formData.items.map((item) => ({
          productId: item.productId,
          quantityToTransfer: Number(item.quantityToTransfer),
          transferSource: item.transferSource,
          notes: item.notes || null,
        })),
      };

      await createTransfer(transferData);
      toast.success('Transfer created successfully!');
      navigate('/inventory/transfers');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create transfer');
    } finally {
      setLoading(false);
    }
  };

  const getSourceStoreName = () => {
    const store = stores.find((s) => (s.id || s.Id) === formData.sourceStoreId);
    return store ? store.storeName || store.StoreName : 'Unknown';
  };

  const getDestinationStoreName = () => {
    const store = stores.find((s) => (s.id || s.Id) === formData.destinationStoreId);
    return store ? store.storeName || store.StoreName : 'Unknown';
  };

  return (
    <Box sx={{ background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)', minHeight: '100vh', pt: 3, pb: 8 }}>
      <Container maxWidth="lg">
        <form onSubmit={handleSubmit}>
          {/* Header Card */}
          <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ bgcolor: alpha('#1976d2', 0.15), p: 1.2, borderRadius: '12px', mr: 2 }}>
                      <TransferIcon sx={{ color: '#1976d2' }} fontSize="large" />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold" color="primary">
                        New Inventory Transfer
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Transfer inventory from Main Store to other locations
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              }
              sx={{
                background: alpha('#1976d2', 0.08),
                borderBottom: `1px solid ${alpha('#1976d2', 0.15)}`,
              }}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Transfer Date"
                    type="date"
                    value={formData.transferDate}
                    onChange={(e) => handleFormChange('transferDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Source Store (From)"
                    select
                    value={formData.sourceStoreId}
                    onChange={(e) => handleFormChange('sourceStoreId', e.target.value)}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <StoreIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  >
                    {mainStore && (
                      <MenuItem value={mainStore.id || mainStore.Id}>
                        <Chip
                          icon={<StoreIcon fontSize="small" />}
                          label={`${mainStore.storeName || mainStore.StoreName} (Main)`}
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        />
                      </MenuItem>
                    )}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Destination Store (To)"
                    select
                    value={formData.destinationStoreId}
                    onChange={(e) => handleFormChange('destinationStoreId', e.target.value)}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <StoreIcon color="secondary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  >
                    {stores
                      .filter((s) => (s.id || s.Id) !== formData.sourceStoreId && !(s.isMain || s.IsMain))
                      .map((store) => (
                        <MenuItem key={store.id || store.Id} value={store.id || store.Id}>
                          {store.storeName || store.StoreName}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Transfer Reason"
                    value={formData.transferReason}
                    onChange={(e) => handleFormChange('transferReason', e.target.value)}
                    placeholder="e.g., Restocking, Customer demand"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Items Table */}
          <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InventoryIcon sx={{ color: '#1976d2', mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      Transfer Items
                    </Typography>
                    {formData.items.length > 0 && (
                      <Chip label={`${formData.items.length} item(s)`} size="small" color="primary" sx={{ ml: 2 }} />
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                    disabled={!formData.sourceStoreId}
                    sx={{ borderRadius: 1.5, textTransform: 'none' }}
                  >
                    Add Item
                  </Button>
                </Box>
              }
              sx={{
                background: alpha('#1976d2', 0.08),
                borderBottom: `1px solid ${alpha('#1976d2', 0.15)}`,
              }}
            />
            <CardContent sx={{ p: 0 }}>
              {formData.items.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No items added yet. Click "Add Item" to start.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Available</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Quantity</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ minWidth: 200 }}>
                            <ProductSelect
                              value={item.productId ? { value: item.productId, label: item.productName } : null}
                              onChange={(product) => handleProductSelect(index, product)}
                              loadOptions={loadProductOptions}
                              menuPortalTarget={document.body}
                              placeholder="Select product..."
                            />
                          </TableCell>
                          <TableCell align="center">
                            {item.availability ? (
                              <Box>
                                <Typography variant="caption" display="block" color="success.main" fontWeight="bold">
                                  Official: {item.availability.officialInventory || 0}
                                </Typography>
                                <Typography variant="caption" display="block" color="warning.main" fontWeight="bold">
                                  Unofficial: {item.availability.unofficialInventory || 0}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Select product
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ minWidth: 120 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantityToTransfer}
                              onChange={(e) => handleItemChange(index, 'quantityToTransfer', e.target.value)}
                              inputProps={{ min: 1, step: 1 }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 150 }}>
                            <TextField
                              select
                              size="small"
                              fullWidth
                              value={item.transferSource}
                              onChange={(e) => handleItemChange(index, 'transferSource', e.target.value)}
                            >
                              <MenuItem value="MainStore">
                                <Chip label="Official" size="small" color="success" />
                              </MenuItem>
                              <MenuItem value="VirtualStore">
                                <Chip label="Unofficial" size="small" color="warning" />
                              </MenuItem>
                            </TextField>
                          </TableCell>
                          <TableCell sx={{ minWidth: 150 }}>
                            <TextField
                              size="small"
                              fullWidth
                              value={item.notes}
                              onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                              placeholder="Optional notes"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Summary & Actions */}
          <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#2196f3', 0.05) }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Transfer Summary
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="body2">From:</Typography>
                      <Typography variant="body2" fontWeight="bold">{getSourceStoreName()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="body2">To:</Typography>
                      <Typography variant="body2" fontWeight="bold">{getDestinationStoreName()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="body2">Total Items:</Typography>
                      <Chip label={formData.items.length} size="small" color="primary" />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="body2">Total Quantity:</Typography>
                      <Chip
                        label={formData.items.reduce((sum, item) => sum + Number(item.quantityToTransfer || 0), 0)}
                        size="small"
                        color="secondary"
                      />
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', justifyContent: 'center' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={loading || formData.items.length === 0}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        py: 1.5,
                        boxShadow: 3,
                      }}
                    >
                      {loading ? 'Creating Transfer...' : 'Create Transfer'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      fullWidth
                      onClick={() => navigate('/inventory/transfers')}
                      disabled={loading}
                      sx={{
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        py: 1.5,
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
}

export default CreateTransferPage;
